import React, { useMemo, useState } from "react";
import { warrantyFor } from "../../lib/brand";
import {
  ShieldAlert, ShieldCheck, FileText, X, Copy, Check, Download, ArrowRight,
} from "lucide-react";

const API_BASE = process.env.REACT_APP_BACKEND_URL || "";

/**
 * WarrantyClaimCard — proactive warranty claim assistant.
 *
 * Renders on the Care tab. Reads the pack's SoH + degradation velocity
 * and computes how many months until it crosses the warranty floor.
 * Behavior tiers:
 *   · >= 24 months to floor  → renders as a small "healthy" note
 *   · 6–24 months to floor   → renders as a "watch this" amber card
 *   · <  6 months to floor   → renders as a red "start your claim" card
 *
 * Clicking "Draft claim" opens a modal with a pre-filled service-center
 * email, one-click PDF download of the signed certified passport, and
 * a copy-to-clipboard button so the user can paste it wherever they
 * need to (Tesla Service, an insurance adjuster, small-claims filing).
 */
export const WarrantyClaimCard = ({ vehicle, buildSnapshot }) => {
  const serviceLabel = vehicle?.brand === "ford" ? "Ford Service" : "Tesla Service";
  const soh = vehicle?.sohCurrent ?? null;
  const floor = vehicle?.warranty?.threshold ?? 70;
  const velocity = Math.max(0.02, Math.abs(vehicle?.delta30d ?? 0.2)); // %/mo
  const gap = soh != null ? soh - floor : null;
  const monthsToFloor =
    gap == null ? null : Math.max(0, Math.round(gap / velocity));

  const tier =
    monthsToFloor == null   ? "unknown"
    : monthsToFloor < 6     ? "red"
    : monthsToFloor < 24    ? "amber"
    :                         "green";

  // Only draw attention when the timing is meaningful.
  if (tier === "unknown" || tier === "green") {
    // Render a compact "on track" note so users know the assistant is
    // there — makes the presence of the feature discoverable without
    // being noisy on healthy packs.
    return (
      <div
        className="rounded-xl border p-3 flex items-center gap-3"
        style={{ borderColor: "rgba(57,255,20,0.20)", background: "rgba(57,255,20,0.03)" }}
        data-testid="warranty-claim-healthy"
      >
        <div
          className="w-8 h-8 rounded-md flex items-center justify-center border shrink-0"
          style={{ background: "rgba(57,255,20,0.12)", borderColor: "rgba(57,255,20,0.35)" }}
        >
          <ShieldCheck className="w-4 h-4" style={{ color: "#39FF14" }} strokeWidth={2.4} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-[13px] text-white font-semibold leading-tight">
            Warranty on track
          </div>
          <div className="font-mono text-[10px] text-zinc-500 leading-tight mt-0.5">
            {monthsToFloor != null
              ? `~${monthsToFloor} months of headroom at current degradation rate.`
              : "Not enough telemetry to project yet."}
          </div>
        </div>
      </div>
    );
  }

  return <WarrantyClaimActive
    tier={tier}
    monthsToFloor={monthsToFloor}
    soh={soh}
    floor={floor}
    velocity={velocity}
    vehicle={vehicle}
    buildSnapshot={buildSnapshot}
  />;
};

// -------------------------------------------------------------------------
const WarrantyClaimActive = ({ tier, monthsToFloor, soh, floor, velocity, vehicle, buildSnapshot }) => {
  const serviceLabel = vehicle?.brand === "ford" ? "Ford Service" : "Tesla Service";
  const tone = tier === "red" ? "#FF6B57" : "#FFB020";
  const [open, setOpen] = useState(false);

  return (
    <>
      <div
        className="rounded-2xl border p-4"
        style={{
          borderColor: `${tone}55`,
          background: `linear-gradient(135deg, ${tone}12 0%, rgba(255,255,255,0.02) 100%)`,
        }}
        data-testid="warranty-claim-active"
      >
        <div className="flex items-start gap-3">
          <div
            className="w-9 h-9 rounded-lg flex items-center justify-center border shrink-0"
            style={{ background: `${tone}18`, borderColor: `${tone}55` }}
          >
            <ShieldAlert className="w-4 h-4" style={{ color: tone }} strokeWidth={2.4} />
          </div>
          <div className="min-w-0 flex-1">
            <div className="font-mono text-[9px] uppercase tracking-[0.22em] text-zinc-500">
              Warranty {tier === "red" ? "action window" : "watch"}
            </div>
            <div className="text-[13px] text-white font-semibold leading-snug mt-0.5">
              {tier === "red"
                ? `Your pack is on track to cross the ${floor}% warranty floor in ~${monthsToFloor} months.`
                : `Your pack could reach the ${floor}% floor within ~${monthsToFloor} months at current velocity.`}
            </div>
            <div className="text-[11px] text-zinc-400 leading-relaxed mt-1.5">
              We can auto-draft a warranty claim to {serviceLabel} and attach a
              cryptographically-signed passport as evidence — the same PDF a service
              advisor can verify at <span className="font-mono text-zinc-300">eviq.pro</span>.
            </div>
          </div>
        </div>

        <button
          type="button"
          onClick={() => setOpen(true)}
          className="mt-3 w-full rounded-full brand-gradient text-black font-semibold px-4 py-2.5 text-sm inline-flex items-center justify-center gap-2 hover:opacity-90 transition-opacity"
          data-testid="warranty-open-drafter"
        >
          <FileText className="w-4 h-4" strokeWidth={2.4} />
          Draft warranty claim
          <ArrowRight className="w-4 h-4" strokeWidth={2.4} />
        </button>
      </div>

      {open && (
        <ClaimDrafterModal
          onClose={() => setOpen(false)}
          vehicle={vehicle}
          buildSnapshot={buildSnapshot}
          soh={soh}
          floor={floor}
          velocity={velocity}
          monthsToFloor={monthsToFloor}
        />
      )}
    </>
  );
};

// -------------------------------------------------------------------------
const ClaimDrafterModal = ({ onClose, vehicle, buildSnapshot, soh, floor, velocity, monthsToFloor }) => {
  const serviceLabel = vehicle?.brand === "ford" ? "Ford Service" : "Tesla Service";
  const [copied, setCopied] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [pdfPassportId, setPdfPassportId] = useState(null);

  const emailBody = useMemo(() => {
    const yrs = warrantyFor(vehicle).years;
    const kMiles = (warrantyFor(vehicle).miles / 1000).toFixed(0);
    const lines = [
      `RE: Formal Request for High-Voltage Battery Evaluation and Diagnosis`,
      `VIN ${vehicle?.vin || "—"}`,
      ``,
      `Dear ${serviceLabel} Team,`,
      ``,
      `I am writing to formally request a comprehensive evaluation and diagnosis of the`,
      `high-voltage battery in the vehicle referenced below. This request is supported by`,
      `independent telemetry analysis certified under the EVIQ BATTERY VERIFIED™ program;`,
      `the certified Battery Passport report is enclosed for your records.`,
      ``,
      `VEHICLE INFORMATION`,
      `  VIN:               ${vehicle?.vin || "—"}`,
      `  Model / Year:      ${vehicle?.year || "—"} ${vehicle?.model || "—"}${vehicle?.trim ? " · " + vehicle.trim : ""}`,
      `  Odometer:          ${(vehicle?.odometer || 0).toLocaleString()} miles`,
      ``,
      `DOCUMENTED BATTERY CONDITION — EVIQ BATTERY VERIFIED™`,
      `  Current EVIQ Estimated SoH:     ${soh}%`,
      `  Applicable warranty threshold:  ${floor}%`,
      `  Remaining margin:               ${(soh - floor).toFixed(1)} percentage points`,
      `  Observed 30-day degradation:    ${velocity.toFixed(2)}% per month`,
      `  Projected threshold crossing:   ~${monthsToFloor} months at the current trajectory`,
      ``,
      `REQUESTED ACTION`,
      `  1. Schedule a high-voltage battery evaluation and diagnostic at my nearest`,
      `     ${serviceLabel} Center;`,
      `  2. Provide a written summary of the diagnostic findings; and`,
      `  3. Confirm the applicability of coverage under the ${yrs}-year / ${kMiles},000-mile`,
      `     Battery & Drive Unit Limited Warranty.`,
      ``,
      `The enclosed EVIQ BATTERY VERIFIED™ report is cryptographically signed`,
      `(HMAC-SHA256) and may be independently verified at:`,
      `https://eviq.pro/api/battery/verify/{passport_id}`,
      ``,
      `I appreciate your prompt attention to this matter and would welcome confirmation`,
      `of a service appointment at your earliest availability.`,
      ``,
      `Respectfully,`,
      ``,
      `[Your name]`,
      `Owner, ${vehicle?.year || ""} ${vehicle?.brand === "ford" ? "Ford" : vehicle?.brand === "volvo" ? "Volvo" : "Tesla"} ${vehicle?.model || ""}`.trim(),
      ``,
    ];
    return lines.join("\n");
  }, [vehicle, soh, floor, velocity, monthsToFloor]);

  const subject = `Formal Request — HV Battery Evaluation & Diagnosis · VIN ${vehicle?.vin || ""} · EVIQ BATTERY VERIFIED™`;
  const mailtoHref = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(emailBody)}`;

  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(emailBody);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch { /* ignore */ }
  };

  const onDownloadPdf = async () => {
    setDownloading(true);
    try {
      const snapshot = buildSnapshot(vehicle);
      const res = await fetch(`${API_BASE}/api/battery/certify`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(snapshot),
      });
      if (!res.ok) throw new Error("certify_failed");
      setPdfPassportId(res.headers.get("X-EVIQ-Passport-Id") || null);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `EVIQ-Warranty-Claim-${vehicle?.vin?.slice(-8) || "vehicle"}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 4000);
    } catch { /* ignore */ }
    finally { setDownloading(false); }
  };

  return (
    <div
      className="fixed inset-0 z-[65] bg-black/80 backdrop-blur-sm flex items-end sm:items-center justify-center p-3"
      onClick={onClose}
      data-testid="warranty-drafter-overlay"
    >
      <div
        className="w-full max-w-lg max-h-[92vh] overflow-y-auto rounded-2xl border border-white/10 bg-[#0a0a0a]"
        onClick={(e) => e.stopPropagation()}
        data-testid="warranty-drafter-modal"
      >
        <div className="sticky top-0 z-10 bg-[#0a0a0a] border-b border-white/10 px-5 py-4 flex items-start justify-between">
          <div>
            <div className="font-mono text-[9px] uppercase tracking-[0.22em] text-zinc-500">
              Warranty claim draft
            </div>
            <h3 className="font-display text-lg text-white font-semibold mt-1">
              Ready to send to {serviceLabel}
            </h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="shrink-0 rounded-full p-1 hover:bg-white/[0.06] text-zinc-400 hover:text-white"
            aria-label="Close"
            data-testid="warranty-drafter-close"
          >
            <X className="w-4 h-4" strokeWidth={2.4} />
          </button>
        </div>

        <div className="p-5 space-y-4">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-wider text-zinc-500 mb-1">
              Subject
            </div>
            <div className="rounded-lg border border-white/10 bg-black/40 px-3 py-2 text-[13px] text-white" data-testid="warranty-subject">
              {subject}
            </div>
          </div>

          <div>
            <div className="font-mono text-[10px] uppercase tracking-wider text-zinc-500 mb-1">
              Body
            </div>
            <pre
              className="rounded-lg border border-white/10 bg-black/40 px-3 py-3 text-[11px] leading-relaxed text-zinc-200 whitespace-pre-wrap max-h-72 overflow-y-auto font-mono"
              data-testid="warranty-body"
            >
{emailBody}
            </pre>
          </div>

          {pdfPassportId && (
            <div className="rounded-lg border border-[#39FF14]/30 bg-[#39FF14]/[0.06] px-3 py-2 text-[11px] text-[#39FF14] font-mono" data-testid="warranty-pdf-ok">
              PDF certificate signed &amp; downloaded · passport {pdfPassportId}
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 pt-1">
            <button
              type="button"
              onClick={onDownloadPdf}
              disabled={downloading}
              data-testid="warranty-download-pdf"
              className="rounded-full border border-white/15 px-4 py-2 text-xs text-white inline-flex items-center justify-center gap-1.5 hover:bg-white/[0.04] disabled:opacity-50"
            >
              <Download className="w-3.5 h-3.5" strokeWidth={2.4} />
              {downloading ? "Signing…" : "Download PDF"}
            </button>
            <button
              type="button"
              onClick={onCopy}
              data-testid="warranty-copy-email"
              className="rounded-full border border-white/15 px-4 py-2 text-xs text-white inline-flex items-center justify-center gap-1.5 hover:bg-white/[0.04]"
            >
              {copied ? <Check className="w-3.5 h-3.5" style={{ color: "#39FF14" }} strokeWidth={2.4} /> : <Copy className="w-3.5 h-3.5" strokeWidth={2.4} />}
              {copied ? "Copied!" : "Copy email"}
            </button>
            <a
              href={mailtoHref}
              data-testid="warranty-open-mail"
              className="rounded-full brand-gradient text-black font-semibold px-4 py-2 text-xs inline-flex items-center justify-center gap-1.5 hover:opacity-90"
            >
              Open mail
              <ArrowRight className="w-3.5 h-3.5" strokeWidth={2.4} />
            </a>
          </div>

          <p className="text-[10px] text-zinc-500 leading-relaxed pt-2 border-t border-white/5">
            The PDF is HMAC-SHA256 signed and encrypted (no-copy / no-modify).
            A service advisor can verify it at
            <span className="font-mono text-zinc-400"> eviq.pro/api/battery/verify/{"{passport_id}"}</span>.
          </p>
        </div>
      </div>
    </div>
  );
};
